import React, { useState, useEffect } from 'react';
import { X, Trophy, Zap, Wine, TrendingUp, MessageSquare, Globe, Star, Award, Target } from 'lucide-react';
import { calculateUserStats, getUserActivities, checkAndAwardAchievements, getUserProfile } from '../services/firestore';

// Drunk-style achievement definitions
const ACHIEVEMENTS = {
  'first-post': {
    icon: '🚀',
    title: 'First Regret',
    description: 'Posted your first drunk idea',
    color: 'from-blue-500 to-cyan-500'
  },
  'idea-machine': {
    icon: '🤖',
    title: 'Idea Machine',
    description: 'Posted 10+ startup ideas',
    color: 'from-purple-500 to-pink-500'
  },
  'viral-drunk': {
    icon: '🔥',
    title: 'Viral Drunk',
    description: 'Got 100+ upvotes on an idea',
    color: 'from-orange-500 to-red-500'
  },
  'storyteller': {
    icon: '📖',
    title: 'Bar Storyteller',
    description: 'Shared 5+ drunk coding stories',
    color: 'from-amber-500 to-yellow-500'
  },
  'domain-collector': {
    icon: '💀',
    title: 'Domain Collector',
    description: 'Added 5+ domains to graveyard',
    color: 'from-slate-500 to-gray-600'
  },
  'supportive-drunk': {
    icon: '🍺',
    title: 'Supportive Drunk',
    description: 'Supported 50+ stories',
    color: 'from-green-500 to-emerald-500'
  },
  'vote-master': {
    icon: '👍',
    title: 'Vote Master',
    description: 'Voted on 100+ ideas',
    color: 'from-indigo-500 to-purple-500'
  },
  'night-owl': {
    icon: '🦉',
    title: 'Night Owl',
    description: 'Posted between 2am-4am',
    color: 'from-violet-500 to-purple-600'
  }
};

// Drunk rating levels
const RATING_LEVELS = [
  { min: 0, max: 99, level: 'Sober Newbie', icon: '😐', color: 'text-slate-400' },
  { min: 100, max: 499, level: 'Tipsy Thinker', icon: '🙂', color: 'text-green-400' },
  { min: 500, max: 999, level: 'Buzzed Builder', icon: '😄', color: 'text-yellow-400' },
  { min: 1000, max: 2499, level: 'Wasted Wizard', icon: '🤪', color: 'text-orange-400' },
  { min: 2500, max: 4999, level: 'Smashed Savant', icon: '😵', color: 'text-red-400' },
  { min: 5000, max: Infinity, level: 'Blackout Legend', icon: '💀', color: 'text-purple-400' }
];

const UserProfile = ({ isOpen, onClose, userId, userName, userEmail }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [userStats, setUserStats] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [earnedAchievements, setEarnedAchievements] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user data when modal opens
  useEffect(() => {
    if (isOpen && userId) {
      loadUserData();
    }
  }, [isOpen, userId]);

  const loadUserData = async () => {
    setLoading(true);
    try {
      // Get user profile from Firestore
      const profile = await getUserProfile(userId);
      setUserProfile(profile);

      // Calculate stats
      const stats = await calculateUserStats(userId);
      setUserStats(stats);

      // Get recent activities
      const activities = await getUserActivities(userId);

      // If no activities yet, create placeholder activities from user stats
      if (activities.length === 0 && (stats.ideasPosted > 0 || stats.storiesShared > 0 || stats.domainsAdded > 0)) {
        const placeholderActivities = [];
        if (stats.ideasPosted > 0) {
          placeholderActivities.push({
            type: 'idea',
            icon: '🚀',
            text: `Posted ${stats.ideasPosted} startup idea${stats.ideasPosted > 1 ? 's' : ''}`,
            time: 'earlier'
          });
        }
        if (stats.storiesShared > 0) {
          placeholderActivities.push({
            type: 'story',
            icon: '📖',
            text: `Shared ${stats.storiesShared} story${stats.storiesShared > 1 ? 'ies' : ''}`,
            time: 'earlier'
          });
        }
        if (stats.domainsAdded > 0) {
          placeholderActivities.push({
            type: 'domain',
            icon: '💀',
            text: `Added ${stats.domainsAdded} domain${stats.domainsAdded > 1 ? 's' : ''} to graveyard`,
            time: 'earlier'
          });
        }
        setRecentActivities(placeholderActivities);
      } else {
        const formattedActivities = activities.map(activity => {
          const icons = {
            'idea': '🚀',
            'story': '📖',
            'domain': '💀',
            'vote': '👍',
            'comment': '💬',
            'feature-request': '✨'
          };

          return {
            type: activity.type,
            icon: icons[activity.type] || '📝',
            text: activity.description,
            time: timeAgo(activity.createdAt),
            ...activity.metadata
          };
        });
        setRecentActivities(formattedActivities);
      }

      // Check achievements
      const achievements = await checkAndAwardAchievements(userId, stats);
      setEarnedAchievements(achievements);
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const timeAgo = (date) => {
    if (!date) return 'recently';
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const lockedAchievements = Object.keys(ACHIEVEMENTS).filter(key => !earnedAchievements.includes(key));

  const getRatingLevel = (points) => {
    return RATING_LEVELS.find(level => points >= level.min && points <= level.max);
  };

  if (!isOpen) return null;

  // Show loading state
  if (loading || !userStats || !userProfile) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 relative">
          <button
            onClick={onClose}
            className="absolute top-2 right-2 text-slate-400 hover:text-white transition"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="text-white text-center">Loading profile...</div>
        </div>
      </div>
    );
  }

  // Use displayName from Firestore profile, fallback to email
  const displayName = userProfile?.displayName || userEmail || 'Anonymous';
  const avatarLetter = displayName[0]?.toUpperCase() || 'U';

  const ratingLevel = getRatingLevel(userStats.totalPoints);
  const nextLevel = RATING_LEVELS[RATING_LEVELS.indexOf(ratingLevel) + 1];
  const progressToNext = nextLevel ? ((userStats.totalPoints - ratingLevel.min) / (nextLevel.min - ratingLevel.min)) * 100 : 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-2 md:p-4">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[95vh] md:max-h-[90vh]">
        {/* Close Button - Always Visible */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 md:top-4 md:right-4 text-slate-400 hover:text-white transition z-50 bg-slate-900/80 backdrop-blur-sm rounded-full p-2"
        >
          <X className="w-5 h-5 md:w-6 md:h-6" />
        </button>

        {/* Header */}
        <div className="relative p-4 md:p-8 border-b border-slate-800 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-t-2xl shrink-0">

          <div className="flex flex-col md:flex-row items-center md:items-start gap-4 md:gap-6">
            {/* Avatar */}
            <div className="relative shrink-0">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-3xl md:text-4xl font-bold shadow-2xl border-4 border-slate-700">
                {avatarLetter}
              </div>
              <div className="absolute -bottom-2 -right-2 bg-slate-900 rounded-full p-1.5 md:p-2 border-2 border-amber-500">
                <span className="text-xl md:text-2xl">{ratingLevel.icon}</span>
              </div>
            </div>

            {/* User Info */}
            <div className="flex-1 w-full text-center md:text-left">
              <h2 className="text-xl md:text-3xl font-bold text-white mb-1 break-words">{displayName}</h2>
              <div className={`text-base md:text-lg font-bold ${ratingLevel.color} mb-2 flex items-center justify-center md:justify-start gap-2`}>
                <Trophy className="w-4 h-4 md:w-5 md:h-5" />
                {ratingLevel.level}
              </div>
              <div className="text-xs md:text-sm text-slate-400 mb-3">
                Member since {userStats.memberSince?.toDate ?
                  userStats.memberSince.toDate().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) :
                  new Date(userStats.memberSince).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                }
              </div>

              {/* Points & Progress */}
              <div className="bg-slate-950/50 rounded-lg p-3 md:p-4 border border-slate-800">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs md:text-sm text-slate-400">Drunk Points</span>
                  <span className="text-lg md:text-xl font-bold text-amber-400">{userStats.totalPoints.toLocaleString()}</span>
                </div>
                {nextLevel && (
                  <>
                    <div className="w-full bg-slate-800 rounded-full h-2 mb-1">
                      <div
                        className="bg-gradient-to-r from-amber-500 to-orange-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${progressToNext}%` }}
                      />
                    </div>
                    <div className="text-xs text-slate-500">
                      {nextLevel.min - userStats.totalPoints} points to {nextLevel.level}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-800 px-2 md:px-6 overflow-x-auto shrink-0">
          {['overview', 'achievements', 'activity'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 md:px-6 py-3 md:py-4 font-bold text-xs md:text-sm capitalize transition-all border-b-2 whitespace-nowrap ${
                activeTab === tab
                  ? 'border-amber-500 text-white'
                  : 'border-transparent text-slate-500 hover:text-slate-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content - Scrollable */}
        <div className="p-4 md:p-6 overflow-y-auto flex-1">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={<Zap className="w-5 h-5" />} label="Ideas Posted" value={userStats.ideasPosted} color="from-purple-500 to-pink-500" />
              <StatCard icon={<MessageSquare className="w-5 h-5" />} label="Stories Shared" value={userStats.storiesShared} color="from-blue-500 to-cyan-500" />
              <StatCard icon={<Globe className="w-5 h-5" />} label="Domains Added" value={userStats.domainsAdded} color="from-amber-500 to-orange-500" />
              <StatCard icon={<TrendingUp className="w-5 h-5" />} label="Upvotes Got" value={userStats.upvotesReceived} color="from-green-500 to-emerald-500" />
              <StatCard icon={<Wine className="w-5 h-5" />} label="Supports Given" value={userStats.supportsGiven} color="from-orange-500 to-red-500" />
              <StatCard icon={<MessageSquare className="w-5 h-5" />} label="Comments" value={userStats.commentsPosted} color="from-indigo-500 to-purple-500" />
              <StatCard icon={<Trophy className="w-5 h-5" />} label="Achievements" value={earnedAchievements.length} color="from-yellow-500 to-amber-500" />
              <StatCard icon={<Star className="w-5 h-5" />} label="Total Points" value={userStats.totalPoints} color="from-amber-500 to-yellow-500" />
            </div>
          )}

          {activeTab === 'achievements' && (
            <div>
              <div className="mb-6">
                <h3 className="text-base md:text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Award className="w-4 h-4 md:w-5 md:h-5 text-amber-400" />
                  Unlocked ({earnedAchievements.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  {earnedAchievements.map(key => {
                    const achievement = ACHIEVEMENTS[key];
                    return (
                      <div key={key} className="bg-slate-950/50 border border-slate-700 rounded-lg p-3 md:p-4 hover:border-amber-500/50 transition">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg bg-gradient-to-br ${achievement.color} flex items-center justify-center text-xl md:text-2xl shadow-lg shrink-0`}>
                            {achievement.icon}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="font-bold text-white mb-1 text-sm md:text-base">{achievement.title}</h4>
                            <p className="text-xs text-slate-400">{achievement.description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <h3 className="text-base md:text-lg font-bold text-slate-500 mb-4 flex items-center gap-2">
                  <Target className="w-4 h-4 md:w-5 md:h-5" />
                  Locked ({lockedAchievements.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  {lockedAchievements.map(key => {
                    const achievement = ACHIEVEMENTS[key];
                    return (
                      <div key={key} className="bg-slate-950/30 border border-slate-800 rounded-lg p-3 md:p-4 opacity-50">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-slate-800 flex items-center justify-center text-xl md:text-2xl grayscale shrink-0">
                            {achievement.icon}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="font-bold text-slate-400 mb-1 text-sm md:text-base">???</h4>
                            <p className="text-xs text-slate-600">{achievement.description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-3">
              {recentActivities.length === 0 ? (
                <div className="text-slate-500 text-center py-8">No activity yet. Start posting ideas!</div>
              ) : (
                recentActivities.map((activity, idx) => (
                  <div key={idx} className="bg-slate-950/50 border border-slate-800 rounded-lg p-4 flex items-center gap-4">
                    <div className="text-2xl">{activity.icon}</div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{activity.text}</p>
                      <p className="text-xs text-slate-500">{activity.time}</p>
                    </div>
                    {activity.votes && (
                      <div className="text-emerald-400 font-bold text-sm">+{activity.votes} votes</div>
                    )}
                    {activity.supports && (
                      <div className="text-amber-400 font-bold text-sm">+{activity.supports} supports</div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, color }) => (
  <div className="bg-slate-950/50 border border-slate-800 rounded-lg p-3 md:p-4 hover:border-slate-700 transition">
    <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center text-white mb-2 md:mb-3`}>
      {React.cloneElement(icon, { className: 'w-4 h-4 md:w-5 md:h-5' })}
    </div>
    <div className="text-xl md:text-2xl font-bold text-white mb-1">{value.toLocaleString()}</div>
    <div className="text-xs text-slate-500 font-medium">{label}</div>
  </div>
);

export default UserProfile;
